import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import config from './config';
import { auth } from './firebase';

const USER_ID_RESOLVE_MAX_ATTEMPTS = 20;
const USER_ID_RESOLVE_DELAY_MS = 1500;

// Returns the backend user id for the signed-in caregiver. Normally it is
// already in localStorage (Login stores it); if not, it is resolved from the
// signed-in email with retries, and kept in sync across tabs / focus changes.
export default function useActiveUserId() {
  const [activeUserId, setActiveUserId] = useState(() => localStorage.getItem('userId') || '');
  const resolveUserErrorCountRef = useRef(0);

  const resolveUserIdByEmail = useCallback(async (email) => {
    const normalizedEmail = email?.trim().toLowerCase();
    if (!normalizedEmail) {
      return '';
    }

    try {
      const res = await axios.get(`${config.API_BASE_URL}/login`, {
        params: { email: normalizedEmail },
      });
      const userId = res?.data?.id ? String(res.data.id) : '';
      if (!userId) {
        return '';
      }

      localStorage.setItem('userId', userId);
      return userId;
    } catch (err) {
      if (resolveUserErrorCountRef.current < 2) {
        console.error('Error resolving user id:', err);
        resolveUserErrorCountRef.current += 1;
      }
      return '';
    }
  }, []);

  useEffect(() => {
    if (activeUserId) {
      return undefined;
    }

    let canceled = false;

    const resolveUserId = async (attempt = 0) => {
      if (canceled) {
        return;
      }

      const storedUserId = localStorage.getItem('userId') || '';
      if (storedUserId) {
        setActiveUserId(storedUserId);
        return;
      }

      const resolvedUserId = await resolveUserIdByEmail(
        auth.currentUser?.email || localStorage.getItem('authEmail') || ''
      );

      if (resolvedUserId) {
        if (!canceled) {
          setActiveUserId(resolvedUserId);
        }
        return;
      }

      if (attempt < USER_ID_RESOLVE_MAX_ATTEMPTS - 1) {
        setTimeout(() => resolveUserId(attempt + 1), USER_ID_RESOLVE_DELAY_MS);
      }
    };

    resolveUserId();

    return () => {
      canceled = true;
    };
  }, [activeUserId, resolveUserIdByEmail]);

  useEffect(() => {
    const syncUserId = () => {
      const userId = localStorage.getItem('userId') || '';
      setActiveUserId((prev) => (prev === userId ? prev : userId));
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        syncUserId();
      }
    };

    window.addEventListener('focus', syncUserId);
    window.addEventListener('storage', syncUserId);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', syncUserId);
      window.removeEventListener('storage', syncUserId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return activeUserId;
}

// Point the chat page at an existing session before navigating to /chat.
// Navigation is done client-side so the shared avatar keeps running.
export const prepareSessionResume = (sessionId) => {
  localStorage.setItem('sessionId', sessionId);
  localStorage.setItem('sessionIsNew', 'false');
};
